import { Screen } from "config/types"
import { FormattedMessage } from "react-intl"
import { Button } from "./paygate-2screen/button"
import { TextInput } from "../atoms/text-input"
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react"
import { useSurveyStorage } from "lib/survey-storage"
import axios from "axios"
import { CountrySelect } from "./country-select"
import { LanguagesSelect } from "./languages-select"
import { sha256Safe } from "../../lib/common"

interface Props {
  screen: Screen
  isActive: boolean
  onClick: (
    formCid?: string,
    reply?: string | number | string[] | number[],
    screenIndex?: number,
    additionalParameters?: Record<string, string | null | number>,
  ) => void
}

export function TextInputQuestion(props: Props) {
  const { text, buttonText, image, id, customProperties } = props.screen
  const [value, setValue] = useState("")
  const storage = useSurveyStorage()
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    const fieldName = (customProperties?.fieldName as string) ?? id

    if (!value || !fieldName) return

    storage.setItem(fieldName, value)

    if (typeof customProperties?.submitUrl === "string") {
      try {
        await axios.post(customProperties.submitUrl, {
          ...storage.data,
          [fieldName]: value,
        })
      } catch {}
    }

    if (customProperties?.inputType === "email") {
      const hashedEmail = await sha256Safe(value)
      console.log(hashedEmail)
      props.onClick(undefined, undefined, undefined, {
        hashed_email_id: hashedEmail,
      })
    } else {
      props.onClick()
    }
  }

  useEffect(() => {
    if (props.isActive) {
      inputRef.current?.focus()
    }
  }, [props.isActive])

  return (
    // @ts-ignore
    <form id={id} onSubmit={handleSubmit}>
      <img src={image} className="w-[300px] h-auto mx-auto" />
      <h1 className="font-quirk text-[32px] leading-[40px] text-center tracking-[0.5px] mb-8">
        <FormattedMessage id={text} />
      </h1>
      {customProperties?.inputType === "languages" && (
        <LanguagesSelect value={value} onChange={setValue} className="mb-4" />
      )}
      {customProperties?.inputType === "country" && (
        <CountrySelect
          value={value}
          onChange={(value) => setValue(value || "")}
          className="mb-4"
        />
      )}
      {(!customProperties?.inputType ||
        !["languages", "country"].includes(
          customProperties.inputType as string,
        )) && (
        <TextInput
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setValue(e.target.value)
          }
          autoFocus
          ref={inputRef}
          className="w-full mb-4"
          name={id}
          id={id}
          type={(customProperties?.inputType as string) ?? "text"}
          placeholder={(customProperties?.placeholder as string) ?? ""}
          required
        />
      )}
      <Button
        theme="black"
        type="submit"
        className={!customProperties?.legalText ? "mb-4" : ""}
      >
        <FormattedMessage id={buttonText} />
      </Button>
      {customProperties?.legalText && (
        <div className="mt-8 text-center text-base text-gray-300">
          <FormattedMessage
            id={customProperties.legalText as string}
            values={{
              terms: (chunks) => (
                <a
                  href="https://pure.app/content/en/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="italic font-serif-italic text-sm"
                >
                  {chunks}
                </a>
              ),
              privacy: (chunks) => (
                <a
                  href="https://pure.app/content/en/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="italic font-serif-italic text-sm"
                >
                  {chunks}
                </a>
              ),
              safety: (chunks) => (
                <a
                  href="https://pure.app/content/en/security"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="italic font-serif-italic text-sm"
                >
                  {chunks}
                </a>
              ),
              guidelines: (chunks) => (
                <a
                  href="https://pure.app/content/en/guidelines"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="italic font-serif-italic text-sm"
                >
                  {chunks}
                </a>
              ),
            }}
          />
        </div>
      )}
    </form>
  )
}
